import { useEffect, useReducer } from 'react';
import { captureException } from '@sentry/nextjs';

import i18n from 'src/utils/translate';
import api from 'src/utils/api';

import {
  FETCH_COMMUNITY_MEMBERS,
  FETCH_COMMUNITY_MEMBERS_SUCCESS,
  FETCH_COMMUNITY_MEMBERS_FAILURE,
} from 'src/modules/community/communityMembers/actionTypes';
import { reducer, initialState } from 'src/modules/community/communityMembers/reducer';
import { setAlert } from 'src/components/alert/actions';
import paginationResultsPerPage from 'src/constants/paginationResultsPerPage';

export const useMembersEndpoint = (communitySlug, currentFilter, isCurrentUserProfileLoading, userIsLoggedIn, currentPage, currentUserId) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    let didCancel = false;

    const fetchData = async () => {
      dispatch({
        type: FETCH_COMMUNITY_MEMBERS,
      });

      try {
        const url = `members/${communitySlug}`;
        const params: { sorting?: string; start?: number; } = {};
        const limit = paginationResultsPerPage.searchMembers;
        const start = limit * (currentPage - 1);

        if (currentFilter) {
          params.sorting = currentFilter;
        }

        if (currentFilter === 'similar-to-me') {
          params['user-id'] = currentUserId;
        }

        if (start > 0) {
          params.start = start;
        }

        const { data: { members, total } } = await api.get(`private/${url}`, { params });
        const membersWithRoles = members.map((member) => ({ ...member, roles: member.roles.map((role) => role.role) }));

        if (!didCancel) {
          dispatch({
            type: FETCH_COMMUNITY_MEMBERS_SUCCESS,
            data: { members: membersWithRoles, total },
            currentFilter: currentFilter ? currentFilter : 'similar-to-me',
            currentPage: currentPage ? currentPage : 1,
          });
        }
      } catch (ex) {
        if (!didCancel) {
          captureException(ex);
          dispatch({ type: FETCH_COMMUNITY_MEMBERS_FAILURE });
          dispatch(setAlert(i18n.t('There was a problem. Please refresh the page and try again.')));
        }
      }
    };

    if (userIsLoggedIn) {
      fetchData();
    }

    return () => {
      didCancel = true; // prevent changing state for unmounted component
    };
  }, [currentFilter, currentPage, currentUserId, communitySlug, isCurrentUserProfileLoading, userIsLoggedIn]);

  return { ...state };
};
